import { getCurrentDate } from "@/utils/common";
import type { FormInstance } from "antd/es/form";

// ==================== 类型定义 ====================

/**
 * 申请人信息数据结构（标准模式）
 */
export interface ApplicantInfo {
  applicantName: string;
  purchaseUserNo: string;
  createDate: string;
  deptName: string;
  deptCode: string;
  postCode: string;
  postName: string;
  purchaseUserEmail: string;
}

/**
 * 员工信息数据结构（RegisteredTab/AssessmentForm 使用）
 */
export interface EmployeeInfo {
  employeeName: string;
  employeeCode: string;
  createDate: string;
  department: string;
  position: string;
  email: string;
}

/**
 * sessionStorage 中存储的用户数据结构
 */
interface UserData {
  userId?: string;
  nickname?: string;
  username?: string;
  deptName?: string;
  deptNo?: string;
  postNo?: string;
  postName?: string;
  email?: string;
}

/**
 * 字段映射配置
 */
export type FieldMappingType = "applicant" | "employee";

const FIELD_MAPPING: Record<
  FieldMappingType,
  Record<string, keyof UserData | "static">
> = {
  applicant: {
    applicantName: "nickname",
    purchaseUserNo: "username",
    createDate: "static",
    deptName: "deptName",
    deptCode: "deptNo",
    postCode: "postNo",
    postName: "postName",
    purchaseUserEmail: "email",
  },
  employee: {
    employeeName: "nickname",
    employeeCode: "username",
    createDate: "static",
    department: "deptName",
    deptNo: "deptNo",
    position: "postName",
    postNo: "postNo",
    purchaseUserId: "userId",
    email: "email",
  },
};

// ==================== 工具函数 ====================

/**
 * 从 sessionStorage 获取用户数据
 * @returns 解析后的用户数据，如果获取失败返回 null
 */
export const getUserDataFromSession = (): UserData | null => {
  const userDataStr = sessionStorage.getItem("user");

  if (!userDataStr) {
    return null;
  }

  try {
    return JSON.parse(userDataStr);
  } catch (error) {
    console.error("解析用户数据失败:", error);
    return null;
  }
};

/**
 * 从用户数据转换为申请人信息
 * @param userData 从 sessionStorage 获取的用户数据
 * @returns 申请人信息对象
 */
export const transformUserDataToApplicantInfo = (
  userData: UserData,
): ApplicantInfo => {
  return {
    applicantName: userData.nickname || "",
    purchaseUserNo: userData.username || "",
    createDate: getCurrentDate(),
    deptName: userData.deptName || "",
    deptCode: userData.deptNo || "",
    postCode: userData.postNo || "",
    postName: userData.postName || "",
    purchaseUserEmail: userData.email || "",
  };
};

/**
 * 从用户数据转换为员工信息
 * @param userData 从 sessionStorage 获取的用户数据
 * @returns 员工信息对象
 */
export const transformUserDataToEmployeeInfo = (
  userData: UserData,
): EmployeeInfo => {
  return {
    employeeName: userData.nickname || "",
    employeeCode: userData.username || "",
    createDate: getCurrentDate(),
    department: userData.deptName || "",
    position: userData.postName || "",
    email: userData.email || "",
  };
};

/**
 * 通用的字段映射转换函数
 * @param userData 用户数据
 * @param mappingType 字段映射类型
 * @returns 转换后的表单字段对象
 */
export const transformUserDataByMapping = (
  userData: UserData,
  mappingType: FieldMappingType,
): Record<string, string> => {
  const mapping = FIELD_MAPPING[mappingType];
  const result: Record<string, string> = {};

  Object.entries(mapping).forEach(([fieldName, sourceKey]) => {
    if (sourceKey === "static") {
      result[fieldName] = getCurrentDate();
    } else {
      result[fieldName] = userData[sourceKey] || "";
    }
  });

  return result;
};

/**
 * 从 sessionStorage 获取用户数据并填充表单信息
 * 支持多种字段映射模式
 * @param formRef 表单实例引用
 * @param mappingType 字段映射类型，默认为 'applicant'
 * @returns 是否成功获取和填充用户信息
 */
export const fillFormWithUserInfo = (
  formRef: React.RefObject<FormInstance>,
  mappingType: FieldMappingType = "applicant",
): boolean => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return false;
  }

  try {
    const userInfo = transformUserDataByMapping(userData, mappingType);
    formRef.current?.setFieldsValue(userInfo);
    return true;
  } catch (error) {
    console.error("填充用户信息失败:", error);
    return false;
  }
};

/**
 * 从 sessionStorage 获取用户数据并填充申请人信息字段
 * 这是 fillFormWithUserInfo 的便利函数，使用 'applicant' 映射
 * @param formRef 表单实例引用
 * @returns 是否成功获取和填充用户信息
 */
export const fillApplicantInfo = (
  formRef: React.RefObject<FormInstance>,
): boolean => {
  return fillFormWithUserInfo(formRef, "applicant");
};

/**
 * 从 sessionStorage 获取用户数据并填充员工信息字段
 * 这是 fillFormWithUserInfo 的便利函数，使用 'employee' 映射
 * @param formRef 表单实例引用
 * @returns 是否成功获取和填充用户信息
 */
export const fillEmployeeInfo = (
  formRef: React.RefObject<FormInstance>,
): boolean => {
  return fillFormWithUserInfo(formRef, "employee");
};

/**
 * 获取申请人信息（用于预填充表单）
 * 这个函数用于那些需要在初始化时获取申请人信息的场景
 * @returns 申请人信息对象，如果获取失败返回 null
 */
export const getApplicantInfo = (): ApplicantInfo | null => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return null;
  }

  try {
    return transformUserDataToApplicantInfo(userData);
  } catch (error) {
    console.error("获取申请人信息失败:", error);
    return null;
  }
};

/**
 * 获取员工信息（用于预填充表单）
 * 这个函数用于那些需要在初始化时获取员工信息的场景
 * @returns 员工信息对象，如果获取失败返回 null
 */
export const getEmployeeInfo = (): EmployeeInfo | null => {
  const userData = getUserDataFromSession();

  if (!userData) {
    return null;
  }

  try {
    return transformUserDataToEmployeeInfo(userData);
  } catch (error) {
    console.error("获取员工信息失败:", error);
    return null;
  }
};
